import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Languages, Star, ArrowRight } from "lucide-react";

const PMM_LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_89891435-6db1-403c-a368-9f416e4fb932/artifacts/ksi5basw_WhatsApp%20Image%202026-08-13%20at%207.58.19%20AM.jpeg";
const FOUNDERS_IMG = "https://static.prod-images.emergentagent.com/jobs/89891435-6db1-403c-a368-9f416e4fb932/images/4f4ab61b7c1b19912d5fa7891e16db3938142cd0f2bfb7e24af61a388d47e479.jpeg";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      nav(`/${u.role}`);
      toast.success(`${t("welcome")}, ${u.name}`);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || err.message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-[#0d2566] md:grid-cols-2">
      {/* Left: form */}
      <div className="relative flex items-center justify-center overflow-hidden p-6">
        <div className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-orange-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -right-16 h-96 w-96 rounded-full bg-blue-500/15 blur-3xl" />
        <div className="relative w-full max-w-md">
          <div className="mb-10 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="rounded-2xl bg-gradient-to-br from-yellow-300 via-yellow-500 to-orange-500 p-0.5 shadow-lg shadow-yellow-500/20">
                <img src={PMM_LOGO} alt="Logo PMM" className="h-11 w-11 rounded-[0.9rem] object-cover" data-testid="login-logo" />
              </div>
              <div className="leading-tight">
                <span className="font-display text-lg font-extrabold uppercase tracking-tight text-white">
                  {t("appName")}
                </span>
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-yellow-400">
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                  {lang === "en" ? "Must Read Fluently" : "Mesti Lancar Baca"}
                </div>
              </div>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLang(lang === "en" ? "ms" : "en")}
              data-testid="lang-toggle"
              className="gap-2 rounded-full border border-white/20 text-blue-100 hover:bg-white/10 hover:text-white"
            >
              <Languages className="h-4 w-4" />
              {t("switchLanguage")}
            </Button>
          </div>

          <h1 className="pmm-pop font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
            {t("welcome")}
            <span className="text-transparent" style={{ backgroundImage: "linear-gradient(180deg,#f2dd8e,#f0a500)", WebkitBackgroundClip: "text", backgroundClip: "text" }}>.</span>
          </h1>
          <p className="mt-3 font-semibold text-blue-200">
            {lang === "en"
              ? "Sign in to manage your child's classes, attendance & fees."
              : "Log masuk untuk urus kelas, kehadiran & yuran anak anda."}
          </p>
          <div className="mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-yellow-400 to-transparent" />

          <div className="pmm-pop pmm-cinematic relative mt-8 rounded-[2rem] border border-white/15 bg-white/5 shadow-2xl backdrop-blur-xl" style={{ animationDelay: "0.15s" }}>
            <div className="p-7 sm:p-8">
              <form onSubmit={onSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-yellow-400">{t("email")}</Label>
                  <Input
                    id="email"
                    data-testid="login-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl border-white/20 bg-white/10 text-white placeholder:text-blue-300/60 focus-visible:ring-yellow-400"
                    placeholder="you@school.my"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-yellow-400">{t("password")}</Label>
                  <Input
                    id="password"
                    data-testid="login-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-xl border-white/20 bg-white/10 text-white focus-visible:ring-yellow-400"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="login-submit"
                  className="h-13 w-full gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 py-3.5 font-display text-base font-extrabold uppercase tracking-wide text-white shadow-2xl shadow-orange-500/40 transition-transform duration-300 hover:scale-[1.02]"
                >
                  {busy ? "…" : t("login")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </form>
              <div className="mt-5 text-center text-sm font-medium text-blue-200">
                {lang === "en" ? "New parent here? " : "Ibu bapa baru? "}
                <Link
                  to="/register"
                  className="font-extrabold text-yellow-400 hover:underline"
                  data-testid="go-register"
                >
                  {t("register")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right: art */}
      <div className="relative hidden overflow-hidden md:block">
        <img
          src={FOUNDERS_IMG}
          alt="Pengasas Program Membaca PMM"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-blue-950 via-blue-950/30 to-transparent" />
        <div className="pmm-float absolute right-8 top-8 rounded-2xl border border-yellow-400/40 bg-blue-950/70 px-5 py-3 text-center shadow-2xl backdrop-blur">
          <div className="font-display text-2xl font-extrabold text-transparent" style={{ backgroundImage: "linear-gradient(180deg,#f2dd8e,#f0a500)", WebkitBackgroundClip: "text", backgroundClip: "text" }}>20,000+</div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-white">{lang === "en" ? "Fluent Readers" : "Pelajar Lancar Membaca"}</div>
        </div>
        <div className="absolute bottom-10 left-10 right-10">
          <div className="max-w-md rounded-[2rem] border border-white/15 bg-white/10 p-7 shadow-2xl backdrop-blur-xl">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="mt-3 font-display text-2xl font-extrabold uppercase leading-tight text-white">
              {lang === "en"
                ? "Reading builds an excellent generation."
                : "Membaca membina generasi cemerlang."}
            </p>
            <p className="mt-2 text-sm font-bold uppercase tracking-widest text-yellow-400">
              {lang === "en" ? "PMM — Since 2007" : "PMM — Sejak 2007"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
